import React, { useState, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  Box,
  Typography,
  alpha,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Fuse, { type FuseResult } from 'fuse.js';

import { PageName } from '../types';

// Define the pages available for searching
const pages = [
  { title: 'Dashboard', path: '/dashboard' },
  { title: 'Cost Analysis', path: '/cost-analysis' },
  { title: 'CoPilot', path: '/copilot' },
  { title: 'Data Sources', path: '/data-sources' },
  { title: 'User Management', path: '/user-management' },
  { title: 'Settings', path: '/settings' },
  { title: 'LLM Observability Traces', path: '/llm-observability/traces' },
];

// Fuse.js options for fuzzy searching
const fuseOptions = {
  keys: ['title'],
  includeScore: true,
  threshold: 0.4,
};

interface HeaderProps {
  toggleSidebar: () => void;
  sidebarOpen: boolean;
  activePage: PageName;
  sx?: SxProps<Theme>;
  userName?: string | null;
  onLogout: () => void;
  userEmail?: string | null;
  userRole?: string | null;
  userProfilePicture?: string | null;
}

const Header: React.FC<HeaderProps> = ({
  toggleSidebar,
  sidebarOpen,
  activePage,
  sx,
  userName,
  onLogout,
  userEmail,
  userRole,
  userProfilePicture,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();
  const navigate = useNavigate();
  const fuse = useMemo(() => new Fuse(pages, fuseOptions), []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<FuseResult<{title: string; path: string;}>[]>([]);

  const open = Boolean(anchorEl);
  const notificationSvgPath = '/icons/notification-2-line.svg';

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    onLogout();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query) {
      const results = fuse.search(query);
      setSuggestions(results);
    } else {
      setSuggestions([]);
    }
  };

  // ✅ THIS FUNCTION IS UPDATED
  const handleSuggestionClick = (path: string) => {
    navigate(path);
    // The search query is no longer cleared, so it stays in the search bar
    setSuggestions([]); // Hide the suggestion dropdown
  };

  // const formatPageTitle = (page: PageName) => {
  //   if (!page) return '';
  // //   if (page.toLowerCase() === 'copilot') {
  // //   return 'CoPilot';
  // // }
  // if (page==='llm-observability'){
  //   return 'LLM Observability';
  // }
  //   return page
  //     .split('-')
  //     .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
  //     .join(' ');
  // };

  const formatPageTitle = (page: PageName) => {
    if (!page) return '';
    if (page === 'llm-observability') {
      return 'LLM Observability';
    }
    if (page === 'llm-observability-traces') {
      return 'Traces';
    }
    return page
      .split('-')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const currentPageTitle = formatPageTitle(activePage);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        color: 'text.primary',
        top: 0,
        borderBottom: `0.7px solid ${
          theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]
        }`,
        ...sx,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64, px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mr: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="toggle sidebar"
              onClick={toggleSidebar}
              sx={{
                mr: 0,
                display: sidebarOpen || isLargeScreen ? 'inline-flex' : 'none',
              }}
            >
              <img
                src="/icons/menu-icon.svg"
                alt="Toggle Sidebar"
                style={{ width: '20px', height: '20px' }}
              />
            </IconButton>

            {/* <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }} noWrap>
              {location.pathname.includes('/data-sources/add') ? (
                <>
                  <span style={{ color: theme.palette.text.secondary }}>Data Sources / </span>
                  <span style={{ color: theme.palette.text.primary }}>Add New Data Sources</span>
                </>
              ) : location.pathname.includes('/data-sources/edit') ? (
                <>
                  <span style={{ color: theme.palette.text.secondary }}>Data Sources / </span>
                  <span style={{ color: theme.palette.text.primary }}>Edit Data Source</span>
                </>
                              ) : location.pathname.includes('/budget-alerts/add') ? ( // <-- ADD THIS CONDITION
                <>
                  <span style={{ color: theme.palette.text.secondary }}>Budget & Alerts / </span>
                  <span style={{ color: theme.palette.text.primary }}>Add New Budget</span>
                </>
              ) : (
                currentPageTitle
              )}
            </Typography> */}
            <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }} noWrap>
  {location.pathname.includes('/data-sources/add') ? (
    <>
      <span style={{ color: theme.palette.text.secondary }}>Data Sources / </span>
      <span style={{ color: theme.palette.text.primary }}>Add New Data Sources</span>
    </>
  ) : location.pathname.includes('/data-sources/edit') ? (
    <>
      <span style={{ color: theme.palette.text.secondary }}>Data Sources / </span>
      <span style={{ color: theme.palette.text.primary }}>Edit Data Source</span>
    </>
  // VVVV ADD THIS NEW CONDITION VVVV
  ) : location.pathname.startsWith('/budget-alerts/edit') ? ( 
    <>
      <span style={{ color: theme.palette.text.secondary }}>Budget & Alerts / </span>
      <span style={{ color: theme.palette.text.primary }}>Edit Budget</span>
    </>
  // ^^^^ END OF NEW CONDITION ^^^^
  ) : location.pathname.includes('/budget-alerts/add') ? (
    <>
      <span style={{ color: theme.palette.text.secondary }}>Budget & Alerts / </span>
      <span style={{ color: theme.palette.text.primary }}>Add New Budget</span>
    </>
  ) : location.pathname.startsWith('/llm-observability/traces') ? (
      <>
          <span style={{ color: theme.palette.text.secondary }}>LLM Observability / </span>
          <span style={{ color: theme.palette.text.primary }}>Traces</span>
      </>
  ) : (
    currentPageTitle
  )}
</Typography>
          </Box>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '10px',
              backgroundColor: alpha(theme.palette.grey[400], 0.12),
              '&:hover': { backgroundColor: alpha(theme.palette.grey[500], 0.2) },
              marginLeft: { xs: 1, sm: 'auto' },
              marginRight: { xs: 1, sm: 2 },
              width: 'auto',
              maxWidth: { xs: '150px', sm: '200px', md: '250px' },
              display: 'flex',
              alignItems: 'center',
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(1),
              height: '30px',
              flexShrink: 0,
            }}
          >
            <img
              src="/icons/search_icon.svg"
              alt="search"
              style={{ width: 18, height: 18, marginLeft: '2px', marginRight: '9px' }}
            />
            <InputBase
              placeholder="Search"
              inputProps={{ 'aria-label': 'search' }}
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
               width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px',
                backgroundColor: 'var(--black-4, rgba(0, 0, 0, 0.04))', color: '#999999',
                fontWeight: '300', '& fieldset': { border: 0 }
              },
              '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' }
            }}
            />
          </Box>
          {suggestions.length > 0 && (
            <Paper
              sx={{
                position: 'absolute',
                zIndex: 1100,
                top: '100%',
                left: 0,
                right: 0,
                mt: 1,
                boxShadow: 3,
                borderRadius: '8px',
              }}
            >
              <List>
                {suggestions.map(({ item }) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton onClick={() => handleSuggestionClick(item.path)}>
                      <ListItemText primary={item.title} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 1 }, flexShrink: 0 }}>
          {/* <IconButton
            color="inherit"
            sx={{ p: 0.75, '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.1) } }}
          >
            <img
              src={process.env.PUBLIC_URL + notificationSvgPath}
              alt="Notifications"
              style={{ width: '30px', height: '30px' }}
            />
          </IconButton> */}
          <IconButton
            color="inherit"
            sx={{ p: 0.5, '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.1) } }}
            onClick={handleAvatarClick}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar
              alt={userName || 'User Avatar'}
              src={userProfilePicture || undefined}
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              }}
            >
              {userName && userName.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1,
                width: 270,
                borderRadius: '16px',
                p: '10px',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body1" fontWeight="400" fontSize="14px">
                  {userName || 'User'}
                </Typography>
                {userRole && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {userRole}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {userEmail || 'user@example.com'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2,mx: -1.3}} />

            <MenuItem
              onClick={handleLogoutClick}
              sx={{
                backgroundColor: '#F9E9E8',
                height: '33px',
                color: '#CA2B20',
                borderRadius: '8px',
                py: 1,
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: '#FDEBEA',
                },
              }}
            >
              <Typography fontWeight="500" fontSize={'14px'}>
                Log Out
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;